import { memo, ReactNode, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { BottomNav } from './BottomNav';
import { UserAvatar } from './UserAvatar';

interface AppShellProps {
  children: ReactNode;
}

/**
 * AppShell - A mobile-first layout shell that handles:
 * - Dynamic viewport height (100dvh with JS fallback)
 * - Safe area insets for notched devices
 * - Scroll isolation to prevent fixed elements from bouncing
 * - Hardware-accelerated fixed positioning
 * 
 * Structure:
 * - Fixed container that fills the viewport
 * - Header (fixed at top with safe-area padding)
 * - Scrollable content area (isolated scroll container)
 * - Bottom navigation (fixed at bottom with safe-area padding)
 * 
 * Note: Viewport height is handled via CSS using var(--app-height, 100dvh)
 * which is set by initViewportHeight() in main.tsx. We avoid using
 * useViewportHeight hook here to prevent unnecessary re-renders when
 * viewport changes (e.g., mobile browser chrome hiding/showing).
 */
export const AppShell = memo(function AppShell({ children }: AppShellProps) {
  // Height is handled by CSS via --app-height variable (set in main.tsx)
  // This avoids React re-renders when viewport changes
  return (
    <div className="app-shell">
      {/* Background gradient layer - sits behind everything */}
      <div className="app-shell-background" aria-hidden="true" />
      
      {/* Main app container */}
      <div className="app-shell-container">
        {/* Fixed Header */}
        <header className="app-shell-header">
          <h1 className="text-xl font-semibold">PadSwipe</h1>
          <UserAvatar />
        </header>

        {/* Scrollable Content Area */}
        <main className="app-shell-content">
          <Suspense fallback={
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          }>
            {children}
          </Suspense>
        </main>

        {/* Fixed Bottom Navigation */}
        <nav className="app-shell-nav">
          <BottomNav />
        </nav>
      </div>
    </div>
  );
});

AppShell.displayName = 'AppShell';

